/**
 * Browser API client — talks ONLY to the Web BFF (`/api/*`).
 *
 * SECURITY INVARIANTS:
 *   - No backend service URLs ever appear here.
 *   - No Supabase, PostgreSQL, or provider credentials in the frontend bundle.
 *   - The CSRF token is acquired from the BFF response body and echoed back on
 *     the x-csrf-token header for state-changing requests. The matching
 *     host-only cookie remains scoped to the API host.
 *   - Credentials are included on every request to send the HttpOnly session cookie.
 */

export interface ApiResult<T> {
  ok: boolean;
  data?: T;
  error?: ApiError;
}

export interface ApiError {
  errorCode: string;
  status: number;
  title?: string;
  details?: string[];
  correlationId?: string;
}

let csrfToken: string | null = null;

export function resolveApiBaseUrl(configuredBaseUrl: string | undefined, browserOrigin: string): string {
  const candidate = configuredBaseUrl?.trim() || browserOrigin;
  const url = new URL(candidate);
  const isLocalDevelopment = url.hostname === "localhost" || url.hostname === "127.0.0.1";
  if (url.protocol !== "https:" && !isLocalDevelopment) {
    throw new Error("The browser API base URL must use HTTPS");
  }
  if (url.pathname !== "/" || url.search || url.hash || url.username || url.password) {
    throw new Error("The browser API base URL must be an origin without path, query, credentials, or fragment");
  }
  return url.origin;
}

function apiUrl(path: string): URL {
  return new URL(
    `/api${path}`,
    resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL, window.location.origin),
  );
}

async function acquireCsrfToken(): Promise<ApiError | null> {
  let res: Response;
  try {
    res = await fetch(apiUrl("/auth/csrf"), {
      method: "GET",
      headers: { accept: "application/json" },
      credentials: "include",
    });
  } catch {
    return { errorCode: "NETWORK", status: 0, title: "network error" };
  }

  const text = await res.text();
  let json: { data?: { csrf?: string }; error?: ApiError } | null = null;
  try {
    json = text ? JSON.parse(text) as { data?: { csrf?: string }; error?: ApiError } : null;
  } catch {
    return { errorCode: "INVALID_RESPONSE", status: res.status, title: "invalid API response" };
  }
  const token = json?.data?.csrf;
  if (!res.ok || !token) {
    return json?.error ?? { errorCode: "CSRF_BOOTSTRAP_FAILED", status: res.status };
  }
  csrfToken = token;
  return null;
}

const STATE_CHANGING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function api<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | undefined>,
): Promise<ApiResult<T>> {
  const url = apiUrl(path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, v);
    }
  }
  const headers: Record<string, string> = { accept: "application/json" };
  if (STATE_CHANGING.has(method)) {
    if (!csrfToken) {
      const csrfError = await acquireCsrfToken();
      if (csrfError) return { ok: false, error: csrfError };
    }
    headers["x-csrf-token"] = csrfToken!;
  }
  if (body !== undefined) headers["content-type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      credentials: "include",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    return { ok: false, error: { errorCode: "NETWORK", status: 0, title: "network error" } };
  }
  const text = await res.text();
  let json: { ok?: boolean; data?: T; error?: ApiError } | null = null;
  try {
    json = text ? JSON.parse(text) as { ok?: boolean; data?: T; error?: ApiError } : null;
  } catch {
    return { ok: false, error: { errorCode: "INVALID_RESPONSE", status: res.status, title: "invalid API response" } };
  }
  if (!res.ok || (json && json.ok === false)) {
    const err = json?.error ?? { errorCode: "HTTP_ERROR", status: res.status };
    if (err.errorCode === "CSRF_TOKEN_INVALID") csrfToken = null;
    return { ok: false, error: err };
  }
  const responseCsrf = (json?.data as { csrf?: unknown } | undefined)?.csrf;
  if (typeof responseCsrf === "string" && responseCsrf) csrfToken = responseCsrf;
  if (path === "/auth/logout") csrfToken = null;
  return { ok: true, data: json?.data as T | undefined };
}

/* ---- typed helpers ---- */

export const authApi = {
  register: (b: RegisterInput) => api<RegisterResult>("POST", "/auth/register", b),
  verifyEmail: (email: string, token: string) => api("POST", "/auth/verify-email", { email, token }),
  login: (email: string, password: string) => api<LoginResult>("POST", "/auth/login", { email, password }),
  logout: () => api("POST", "/auth/logout"),
  session: () => api<SessionResult>("GET", "/auth/session"),
  me: () => api<MeResult>("GET", "/auth/me"),
};

export const propertyApi = {
  list: () => api<PropertyDto[]>("GET", "/properties"),
  get: (id: string) => api<PropertyDto>("GET", `/properties/${id}`),
  create: (b: CreatePropertyInput) => api<PropertyDto>("POST", "/properties", b),
  update: (id: string, b: CreatePropertyInput) => api<PropertyDto>("PUT", `/properties/${id}`, b),
};

export const financeApi = {
  listIncome: (propertyId?: string) => api<IncomeDto[]>("GET", "/finance/income", undefined, { propertyId }),
  recordIncome: (b: RecordIncomeInput) => api("POST", "/finance/income", b),
  listExpenses: (propertyId?: string) => api<ExpenseDto[]>("GET", "/finance/expenses", undefined, { propertyId }),
  recordExpense: (b: RecordExpenseInput) => api("POST", "/finance/expenses", b),
  summary: (propertyId?: string, currency?: string) => api<FinancialSummaryDto>("GET", "/finance/summary", undefined, { propertyId, currency }),
};

export const taxApi = {
  summary: (asOf?: string) => api("GET", "/tax/summary", undefined, { asOf }),
  rules: () => api("GET", "/tax/rules"),
  upsertProfile: (b: TaxProfileInput) => api("PUT", "/tax/profile", b),
};

export const billingApi = {
  plans: () => api<BillingPlanDto[]>("GET", "/billing/plans"),
  entitlement: () => api<BillingEntitlementDto>("GET", "/billing/entitlement"),
  checkout: (b: CheckoutInput) => api<CheckoutSessionDto>("POST", "/billing/checkout", b),
  portal: (b: PortalInput) => api<PortalSessionDto>("POST", "/billing/portal", b),
};

export const notificationsApi = {
  preferences: () => api("GET", "/notifications/preferences"),
  updatePreference: (b: PreferenceInput) => api("PUT", "/notifications/preferences", b),
};

export const insightsApi = {
  healthPortfolio: () => api("GET", "/insights/health-portfolio"),
  performance: () => api("GET", "/insights/performance"),
  performanceFor: (propertyId: string) => api("GET", `/insights/performance/${propertyId}`),
  forecast: (months: number, property?: string) => api("GET", "/insights/forecast", undefined, { months: String(months), property }),
  anomalies: () => api("GET", "/insights/anomalies"),
};

export const integrationsApi = {
  connectors: () => api("GET", "/integrations/connectors"),
  runs: (connector?: string) => api("GET", "/integrations/runs", undefined, { connector }),
  import: (b: ImportInput) => api("POST", "/integrations/import", b),
  credentials: () => api("GET", "/integrations/credentials"),
  storeCredential: (b: CredentialInput) => api("POST", "/integrations/credentials", b),
};

/* ---- shared DTO types (mirror C# service contracts) ---- */

export interface RegisterInput { email: string; password: string; displayName: string; tenantName: string; countryCode: string }
export interface RegisterResult { userId: string; tenantId: string; emailVerificationToken: string }
export interface LoginResult { userId: string; tenantId: string; role: string; expiresAt: string; csrf: string }
export interface MeResult { tenantId: string; userId: string; email: string; role: string }
export interface SessionResult { authenticated: boolean; user?: MeResult }

export interface PropertyDto {
  id: string; tenantId: string; name: string; fullAddress: string; countryCode: string; status: string;
  businessUsePercent: number; configuredMonthlyRent: number | null; currency: string; description: string | null;
  createdAt: string; updatedAt: string;
}
export interface CreatePropertyInput {
  name: string; addressLine: string; suburb?: string; city?: string; postalCode?: string; countryCode: string;
  businessUsePercent: number; configuredMonthlyRent?: number; currency: string; description?: string;
}

export interface IncomeDto { id: string; propertyId: string; amount: number; currency: string; source: string; occurredOn: string; recordedAt: string; note: string | null }
export interface ExpenseDto { id: string; propertyId: string; amount: number; currency: string; category: string; incurredOn: string; recordedAt: string; note: string | null }
export interface FinancialSummaryDto { tenantId: string; propertyId: string | null; totalIncome: number; totalExpenses: number; netPosition: number; currency: string; incomeCount: number; expenseCount: number }

export interface RecordIncomeInput { propertyId: string; amount: number; currency: string; source: string; occurredOn: string; origin: number; note?: string }
export interface RecordExpenseInput { propertyId: string; amount: number; currency: string; category: string; incurredOn: string; origin: number; note?: string }

export interface TaxProfileInput { countryCode: string; filingFrequency: string; gstRegistered: boolean; gstNumber: string | null }
export interface BillingPlanDto {
  id: string;
  code: string;
  name: string;
  currency: string;
  priceMonthly: number;
  billingInterval: string;
  maxProperties: number;
  maxExpensesPerYear: number;
  isCheckoutAvailable: boolean;
}
export interface BillingEntitlementDto {
  planCode: string;
  planName: string;
  state: string;
  priceMonthly: number;
  currency: string;
  billingInterval: string;
  maxProperties: number;
  maxExpensesPerYear: number;
  isActive: boolean;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  canManageBilling: boolean;
}
export interface CheckoutInput { planCode: string; successUrl: string; cancelUrl: string }
export interface CheckoutSessionDto { checkoutUrl: string; sessionId: string; mode: "test" | "live" }
export interface PortalInput { returnUrl: string }
export interface PortalSessionDto { portalUrl: string; mode: "test" | "live" }
export interface PreferenceInput { notificationType: string; preferredChannel: string; emailEnabled: boolean; smsEnabled: boolean; pushEnabled: boolean; inAppEnabled: boolean }
export interface ImportInput { connectorCode: string; externalSourceId: string; payload: string }
export interface CredentialInput { connectorCode: string; label: string; secret: string }
